import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { BiPencil, BiTrash, BiPlus } from "react-icons/bi";

import { API_BASE_URL } from '../utils/urls.js';

const TaskList = () => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  const user = JSON.parse(localStorage.getItem("user"));
  const isAdmin = user?.roleName === "admin";
  const isCaseManager = user?.roleName === "case_manager";
  const canManageTasks = isAdmin || isCaseManager;
  const isFieldAgent = user?.roleName;


  useEffect(() => {
    const fetchAllTasks = async () => {
      const token = localStorage.getItem("token");
      if (!token) {
        setError("Authentication token not found.");
        setLoading(false);
        return;
      }
      try {
        const response = await axios.get(`${API_BASE_URL}/tasks/all`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setTasks(response.data);
      } catch (err) {
        console.error("Error fetching all tasks:", err);
        setError(
          err.response?.data?.message ||
            "Failed to load tasks. Please try again."
        );
      } finally {
        setLoading(false);
      }
    };

    if (canManageTasks) {
      fetchAllTasks();
    } else {
      setError("Access denied. You do not have permission to view this page.");
      setLoading(false);
    }
  }, [canManageTasks]);

  const handleDelete = async (taskId, taskTitle) => {
    if (
      window.confirm(`Are you sure you want to delete the task "${taskTitle}"?`)
    ) {
      const token = localStorage.getItem("token");
      try {
        await axios.delete(`${API_BASE_URL}/tasks/${taskId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setTasks(tasks.filter((task) => task.id !== taskId));
      } catch (err) {
        console.error("Error deleting task:", err);
        setError(err.response?.data?.message || "Failed to delete task.");
      }
    }
  };

  const filteredTasks = tasks.filter(
    (task) =>
      task.task_title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      task.case_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (task.first_name + " " + task.last_name)
        .toLowerCase()
        .includes(searchQuery.toLowerCase()) ||
      task.status?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="p-6 text-center text-gray-500">Loading all tasks...</div>
    );
  }

  if (error) {
    return <div className="p-6 text-center text-red-500">{error}</div>;
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6 flex-wrap">
        <h1 className="text-3xl font-bold text-gray-800 mb-4 sm:mb-0">
          All Scheduled Tasks
        </h1>
        <div className="flex items-center space-x-4">
          <input
            type="text"
            placeholder="Search tasks..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="px-4 py-2 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-300 w-full sm:w-auto"
          />
          {canManageTasks && (
            <Link
              to="/tasks/new"
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg shadow-md hover:bg-blue-700 transition"
            >
              <BiPlus className="mr-2" /> Create New Task
            </Link>
          )}
        </div>
      </div>
      <div className="bg-white rounded-lg shadow-sm overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Title
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Assigned To
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Case
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Due Date
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Status
              </th>
              {canManageTasks && (
                <th scope="col" className="relative px-6 py-3 text-right">
                  <span className="sr-only">Actions</span>Actions
                </th>
              )}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredTasks.length > 0 ? (
              filteredTasks.map((task) => (
                <tr
                  key={task.id}
                  className="hover:bg-gray-50 transition-colors duration-200"
                >
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {task.task_title}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {task.first_name} {task.last_name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {task.case_name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {task.end_time
                      ? new Date(task.end_time).toLocaleDateString()
                      : "N/A"}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        task.status === "completed"
                          ? "bg-green-100 text-green-800"
                          : "bg-yellow-100 text-yellow-800"
                      }`}
                    >
                      {task.status || "Pending"}
                    </span>
                  </td>
                  {(canManageTasks || isFieldAgent) && (
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <Link
                        to={`/tasks/edit/${task.id}`}
                        className="text-blue-600 hover:text-blue-900 mr-4 inline-flex items-center"
                      >
                        <BiPencil className="inline-block h-5 w-5 mr-1" /> Edit
                      </Link>
                      { canManageTasks && (
                      <button
                        onClick={() => handleDelete(task.id, task.task_title)}
                        className="text-red-600 hover:text-red-900 inline-flex items-center"
                      >
                        <BiTrash className="inline-block h-5 w-5 mr-1" /> Delete
                      </button>
                      )}
                    </td>
                  )}
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={canManageTasks ? "6" : "5"}
                  className="px-6 py-4 text-center text-sm text-gray-500"
                >
                  No tasks found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TaskList;
